import { zodResolver } from '@hookform/resolvers/zod'
import type { AnyExceptUndefined } from '@seen/core/types'
import type { AnyFunction } from '@seen/core/utils'
import { isEqual, omit } from 'lodash-es'
import { useEffect, useRef } from 'react'
import {
  type Control,
  type DefaultValues,
  type FieldValues,
  type UseFormClearErrors,
  type UseFormGetFieldState,
  type UseFormGetValues,
  type UseFormHandleSubmit,
  type UseFormRegister,
  type UseFormReset,
  type UseFormResetField,
  type UseFormSetError,
  type UseFormSetFocus,
  type UseFormSetValue,
  type UseFormTrigger,
  type UseFormUnregister,
  type UseFormWatch,
  useFormState,
  useForm as useReactHookForm,
} from 'react-hook-form'
import type { z } from 'zod'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'

// react-hook-form requires form values to not be undefined. This is because React has issues when a form field goes from uncontrolled (undefined value) to controlled (defined value).
export type FormSchema<T extends FormObjectSchema = FormObjectSchema> =
  | T
  | z.ZodEffects<T>
  | z.ZodEffects<z.ZodEffects<T>> // Note: If we need more levels of effects we can add additional nested effects here. If there's a more elegant way to do this please do!
type FormObjectSchema = z.ZodObject<ZodDefinedFields>
type ZodDefinedFields = {
  [k: string]: z.ZodType<Exclude<AnyExceptUndefined, AnyFunction>>
}

interface UseFormProps<TSchema extends FormSchema> {
  schema: TSchema
  defaultValues: z.infer<TSchema>
  disabled?: boolean // Note: There seems to be a bug with react-hook-form where if you initially set disabled to true and then change its value, it will still treat the form as disabled. It seems it's OK to disable the form after the initial render.
  schemaOptions?: Partial<z.ParseParams>
  restorationId?: string
}

// Note: We remove formState from the return type as there are too many footguns with it (see rules here: https://react-hook-form.com/docs/useform/formstate). We use useFormState instead.
export interface UseFormReturn<TSchema extends FormSchema> {
  watch: UseFormWatch<z.infer<TSchema>>
  getValues: UseFormGetValues<z.infer<TSchema>>
  getFieldState: UseFormGetFieldState<z.infer<TSchema>>
  setError: UseFormSetError<z.infer<TSchema>>
  clearErrors: UseFormClearErrors<z.infer<TSchema>>
  setValue: UseFormSetValue<z.infer<TSchema>>
  trigger: UseFormTrigger<z.infer<TSchema>>
  resetField: UseFormResetField<z.infer<TSchema>>
  reset: UseFormReset<z.infer<TSchema>>
  handleSubmit: UseFormHandleSubmit<z.infer<TSchema>>
  unregister: UseFormUnregister<z.infer<TSchema>>
  control: Control<z.infer<TSchema>>
  register: UseFormRegister<z.infer<TSchema>>
  setFocus: UseFormSetFocus<z.infer<TSchema>>
}

export function useForm<TSchema extends FormSchema>({
  schema,
  schemaOptions,
  defaultValues,
  restorationId,
  ...delegated
}: UseFormProps<TSchema>): UseFormReturn<TSchema> {
  const storeValues = useFormStore((state) =>
    restorationId ? state.valuesById[restorationId] : undefined
  )

  const form = useReactHookForm({
    resolver: zodResolver(schema, schemaOptions),
    defaultValues: (storeValues ?? defaultValues) as DefaultValues<
      z.infer<TSchema>
    >,
    ...delegated,
  })

  usePersistToStore(form, restorationId)

  return form
}

function usePersistToStore(
  form: UseFormReturn<any>,
  restorationId: string | undefined
): void {
  const rawFormValues = form.watch()
  const formValuesRef = useRef(rawFormValues)
  if (!isEqual(formValuesRef.current, rawFormValues)) {
    formValuesRef.current = rawFormValues
  }
  const formValues = formValuesRef.current
  const setStoreValues = useFormStore((state) => state.setValues)
  useEffect(() => {
    if (restorationId) {
      setStoreValues(restorationId, formValues)
    }
  }, [formValues, restorationId, setStoreValues])

  const removeStoreValues = useFormStore((state) => state.removeValues)
  const { isSubmitSuccessful } = useFormState({ control: form.control })
  useEffect(() => {
    if (isSubmitSuccessful && restorationId) {
      removeStoreValues(restorationId)
    }
  }, [isSubmitSuccessful, restorationId, removeStoreValues])
}

const initialValuesById: Record<string, FieldValues> = {}

const useFormStore = create(
  devtools(
    persist(
      combine(
        {
          valuesById: initialValuesById,
        },
        (set) => ({
          setValues: (id: string, values: FieldValues) =>
            set((state) => ({
              valuesById: {
                ...state.valuesById,
                [id]: values,
              },
            })),
          removeValues: (id: string) =>
            set((state) => ({
              valuesById: omit(state.valuesById, id),
            })),
        })
      ),
      {
        name: 'form-values',
      }
    )
  )
)
